import { useState } from 'react';
import { Link } from 'react-router-dom';
import { forgotPassword } from '../../services/api';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { Email, ArrowBack } from '@mui/icons-material';

/* ── Design tokens ─────────────────────────────────────────────── */
const PRIMARY = '#6C98AD';
const PRIMARY_HOVER = '#5a8499';
const SECONDARY = '#24272a';
const CARD_SHADOW = '0 4px 20px rgba(108, 152, 173, 0.15)';
const CARD_SHADOW_HOVER = '0 8px 30px rgba(108, 152, 173, 0.25)';
const FONT_HEADING = "'Avenir Next LT Pro', sans-serif";
const FONT_BODY = "'Avenir Next LT Pro', sans-serif";
const RADIUS = '1rem';
const TRANSITION = 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      return 'Email is required';
    }
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    if (emailError) {
      setEmailError(validateEmail(value));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validate email
    const emailErr = validateEmail(email);
    setEmailError(emailErr);

    if (emailErr) {
      return;
    }

    setLoading(true);
    try {
      const result = await forgotPassword(email);
      setSuccess(result?.data?.message || 'If that email exists, a reset link has been sent.');
      setEmail('');
      setEmailError('');
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundImage: `url(${process.env.PUBLIC_URL}/static/images/mstructure_login_bg.jpg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        fontFamily: FONT_BODY,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          pointerEvents: 'none',
        },
      }}
    >
      <Paper
        component="form"
        onSubmit={handleSubmit}
        elevation={0}
        sx={{
          p: { xs: 3, sm: 5 },
          width: '100%',
          maxWidth: 440,
          borderRadius: RADIUS,
          boxShadow: CARD_SHADOW,
          border: '2px solid rgba(108, 152, 173, 0.15)',
          transition: TRANSITION,
          position: 'relative',
          zIndex: 1,
          backgroundColor: '#ffffff',
          '&:hover': {
            boxShadow: CARD_SHADOW_HOVER,
            borderColor: PRIMARY,
          },
        }}
      >
        {/* Logo + heading */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <img
            src={process.env.PUBLIC_URL + '/TALOGOWEBSITE.webp'}
            alt="Tailored Alloys"
            height="40"
            style={{ marginBottom: 16 }}
          />
          <Typography
            variant="h5"
            sx={{ fontWeight: 700, color: SECONDARY, fontFamily: FONT_HEADING }}
          >
            Forgot Password
          </Typography>
          <Typography variant="body2" sx={{ color: PRIMARY, mt: 0.5, fontFamily: FONT_BODY }}>
            Enter your email to receive a reset link
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: RADIUS }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2, borderRadius: RADIUS }}>
            {success}
          </Alert>
        )}

        <TextField
          label="Email"
          type="email"
          value={email}
          onChange={handleEmailChange}
          onBlur={() => setEmailError(validateEmail(email))}
          fullWidth
          required
          error={!!emailError}
          helperText={emailError}
          sx={{
            mb: 3,
            '& .MuiOutlinedInput-root': {
              borderRadius: RADIUS,
              fontFamily: FONT_BODY,
              '&.Mui-focused fieldset': { borderColor: PRIMARY },
            },
            '& .MuiInputLabel-root.Mui-focused': { color: PRIMARY },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Email sx={{ color: PRIMARY }} />
              </InputAdornment>
            ),
          }}
        />

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={loading}
          sx={{
            py: 1.4,
            borderRadius: RADIUS,
            fontWeight: 600,
            fontSize: '1rem',
            fontFamily: FONT_BODY,
            textTransform: 'none',
            background: `linear-gradient(135deg, ${PRIMARY} 0%, #8eb4c7 100%)`,
            boxShadow: '0 4px 12px rgba(108, 152, 173, 0.3)',
            transition: TRANSITION,
            '&:hover': {
              background: `linear-gradient(135deg, ${PRIMARY_HOVER} 0%, ${PRIMARY} 100%)`,
              boxShadow: '0 8px 20px rgba(108, 152, 173, 0.5)',
              transform: 'translateY(-2px)',
            },
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Reset Link'}
        </Button>

        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Link
            to="/admin/login"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              color: PRIMARY,
              textDecoration: 'none',
              fontFamily: FONT_BODY,
              fontSize: '0.9rem',
            }}
          >
            <ArrowBack sx={{ fontSize: 18 }} /> Back to Login
          </Link>
        </Box>
      </Paper>
    </Box>
  );
}
